// Centralized API base URL. Always use relative '/api' so nginx can
// proxy to the backend regardless of environment — VITE_API_URL is
// only an override (e.g. for local dev pointing at a different host).
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ApiError {
    error: string;
    message: string;
    statusCode: number;
    /** Optional machine-readable reason, e.g. EMAIL_NOT_VERIFIED. */
    code?: string;
}

export class ApiClientError extends Error {
    constructor(
        public statusCode: number,
        public error: string,
        message: string,
        public code?: string
    ) {
        super(message);
        this.name = 'ApiClientError';
    }
}

export const AUTH_EXPIRED_EVENT = 'project-manager:auth-expired';

function notifyExpiredSession(response: Response): void {
    if (response.status !== 401 || typeof window === 'undefined') return;

    const pathname = new URL(response.url, window.location.origin).pathname;
    const expectedUnauthorizedRoutes = [
        '/api/auth/login',
        '/api/auth/register',
        '/api/auth/me',
        '/api/auth/forgot-password',
        '/api/auth/reset-password',
    ];

    if (!expectedUnauthorizedRoutes.some((route) => pathname.endsWith(route))) {
        window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        notifyExpiredSession(response);
        const errorData = (await response.json().catch(() => ({
            error: 'Unknown Error',
            message: 'An unexpected error occurred',
            statusCode: response.status,
        }))) as ApiError;

        throw new ApiClientError(
            errorData.statusCode || response.status,
            errorData.error || 'Error',
            errorData.message || 'An unexpected error occurred',
            errorData.code
        );
    }

    return response.json();
}

export async function apiGet<T>(
    endpoint: string,
    /**
     * Optional request options. `signal` lets debounced callers (e.g.
     * the document `@`-mention server-side task search) abort an
     * in-flight request when the user keeps typing. Existing callers
     * pass no second argument and keep the exact previous behaviour.
     */
    init?: { signal?: AbortSignal }
): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        signal: init?.signal,
    });

    return handleResponse<T>(response);
}

export async function apiPost<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    const headers: Record<string, string> = body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {};

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    return handleResponse<T>(response);
}

export async function apiPut<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    return handleResponse<T>(response);
}

export async function apiPatch<T, B = unknown>(endpoint: string, body?: B): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    return handleResponse<T>(response);
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        credentials: 'include',
        // Don't set Content-Type for DELETE without body to avoid Fastify "empty body" error
    });

    return handleResponse<T>(response);
}
